import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL, LIVE } from '../../common/variables';
import augmentProgress from '../../utils/augmentProgress';
import { selectCategory } from '../selectors/categories';
import { selectMerchantId } from '../selectors/merchantSelectors';
import { selectMerchantssId } from '../selectors/merchantssSelectors';

const initialProductsState = {
  isFetchingProducts: true,
  isFetchingProductsPagin: true,
  errorPagin: false,
  paginator: false,
  isIndexed: false,
  isCreating: false,
  isRemoving: false,
  isActivating: false,
  isUpdating: false,
  items: [],
  itemsUpdated: 0,
  merchantId: undefined,
  errorProducts: null,
  errorCreatingProduct: null,
  selectedProduct: null,
  selectedExtraction: null,
  isUpdatingExtraction: false,
  errorUpdatingExtraction: false,
  isDeletingVariation: false,
  errorDeletingVariation: false,
  isCreatingVariation: false,
  errorCreatingVariation: false,
  isOrderingVariation: false,
  errorOrderingVariation: false,
  page: 1,
  showLoadMore: false,
  isOrderingProducts: false,
}

const PRODUCTS_PER_PAGE = 12;

const updateProductExtraction = createAsyncThunk(
  'UPDATE_PRODUCT_EXTRACTION',
  async (arg, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      const { productId, fileBase64, fileName } = arg;

      let res = await fetch(API_URL + '/products/update-2d-extraction-base64', {
        body: JSON.stringify({
          live: LIVE,
          productId: productId,
          fileBase64: fileBase64,
          filename: fileName,
        }),
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        return await res.json();
      }
      return rejectWithValue(res.status);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

function getFile(base64String) {
  const binaryString = window?.atob(base64String);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Crear un objeto File a partir del ArrayBuffer
  const file = new File([bytes.buffer], "filename.png", { type: "image/png" });
  return file;
}

const updateProductVariantExtraction = createAsyncThunk(
  'UPDATE_PRODUCT_EXTRACTION',
  async (arg, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      const { productId, fileBase64, variantId } = arg;

      const file = getFile(fileBase64);

      const formData = new FormData();
      formData.append('file', file);

      let res = await fetch(API_URL + `/products/${productId}/variants/${variantId}/extraction`, {
        body: formData,
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        return await res.json();
      }
      return rejectWithValue(res.status);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const requestProductsPagin = createAsyncThunk(
  'REQUEST_PRODUCTS_PAGIN',
  async (filter, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let res = await fetch(
        API_URL + '/products/search-auth-pagination-params',
        {
          body: JSON.stringify({ ...filter, live: LIVE }),
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status === 200) {
        let result = await res.json();
        return result;
      }
      return rejectWithValue(res.status);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const requestProducts = createAsyncThunk(
  'FETCHING_PRODUCTS',
  async (data, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { token } = state.app;
      const { page } = state.products;


      const selectMerchantsId = selectMerchantssId(state);
      // console.log('selectMerchantsId',selectMerchantsId);
      const merchantId = selectMerchantId(state);
      // console.log('merchantId',merchantId)
      const category = selectCategory(state);

      const paginationByPageNumbers = {
        page: page,
        elements: PRODUCTS_PER_PAGE,
      };

      const filter = {
        fromMerchant: merchantId,
        sortByOrderAsc: true,
      };

      if (category !== null && category !== 'ALL') {
        filter.categories = [category];
      }

      let res = await fetch(API_URL + '/products/search', {
        body: JSON.stringify({ filter, paginationByPageNumbers }),
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        let result = await res.json();
        let pros = result.map((item) => item.product);
        // augmentProgress(80);
        return pros;
      }
      return rejectWithValue(res.status);
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const addProduct = createAsyncThunk(
  'ADD_PRODUCT',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let fd = new FormData();
      fd.append('merchantId', data.merchantId);
      fd.append('file', data.file);
      fd.append('title', data.title);
      fd.append('price', data.price);
      fd.append('description', data.description);
      if (data.category) fd.append('category', data.category);
      fd.append('width', data.dimensions.width);
      fd.append('height', data.dimensions.height);
      fd.append('depth', data.dimensions.depth);
      fd.append('stock', data.stock);
      fd.append('weight', data.weight);
      fd.append('measure', data.measure);
      fd.append('externalBuyUrl', data.externalBuyUrl);
      fd.append('externalBuyEnabled', data.externalBuyEnabled);
      fd.append('live', LIVE);

      const res = await axios.post(API_URL + '/products/add-product', fd, {
        timeout: 20000,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 200) {
        //let result = await res.json()
        return res.data;
      }
      return rejectWithValue({ errorCode: res.status });
    } catch (error) {
      console.log(error);
      if (error.response) {
        // Request made and server responded
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log('Error', error);
      }
      return rejectWithValue({ errorCode: 999 });
    }
  },
);

const requestProduct = createAsyncThunk(
  'REQUEST_PRODUCT',
  async (productId, { getState, rejectWithValue }) => {
    try {
      let { token } = getState().app;
      if (!token) token = sessionStorage.getItem('sessionToken');
      let res = await fetch(API_URL + '/products/get-one-auth', {
        body: JSON.stringify({ live: LIVE, productId: productId }),
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        return await res.json();
      }
      return rejectWithValue(res.status);
    } catch (e) {
      return rejectWithValue(900);
    }
  },
);

const editProductImage = createAsyncThunk(
  'EDIT_PRODUCT_IMAGE',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let fd = new FormData();
      fd.append('file', data.file);
      fd.append('productId', data.id);
      fd.append('live', LIVE + '');
      let res = await fetch(API_URL + '/products/update-product-image', {
        method: 'POST',
        body: fd,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        return await res.json();
      }
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const editProduct = createAsyncThunk(
  'EDIT_PRODUCT',
  async (arg, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { token } = state.app;

      const data = {
        productId: arg.id,
        title: arg.title,
        price: arg.price,
        description: arg.description,
        dimensions: arg.dimensions,
        sold: arg.sold,
        stock: arg.stock,
        externalBuyUrl: arg.externalBuyUrl,
        externalBuyEnabled: arg.externalBuyEnabled,
        live: LIVE,
      };

      if (arg.category) data.category = arg.category;

      data.price = parseFloat(data.price);
      if (!data.price) {
        data.price = 1;
      }

      let res = await fetch(API_URL + '/products/update-product', {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) return await res.json();
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const removeProductById = createAsyncThunk(
  'REMOVE_PRODUCT',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let res = await fetch(API_URL + '/products/delete-product', {
        method: 'POST',
        body: JSON.stringify({ live: LIVE, productId: data.id }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) return { productId: data.id };
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const productUploadExtractions = createAsyncThunk(
  'UPLOAD_EXTRACTION',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      const { id, files } = data;
      const promises = files.map((file) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('productId', id);
        fd.append('live', LIVE + '');

        return fetch(API_URL + '/products/upload-2d-extraction', {
          body: fd,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .then((response) => {
            if (response.status === 200)
              return response.json().then((result) => {
                return result;
              });
          })
          .catch((error) => {
            throw new Error('error');
          });
      });

      let result = await Promise.all(promises);
      //console.log('Result Promise:',result)
      return result;

      // return rejectWithValue({error:true,errorCode:900})
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

// const productUploadExtractions_3d = createAsyncThunk(
//   'UPLOAD_EXTRACTION_3D',
//   async (data, { getState, rejectWithValue }) => {
//     try {
//       const { token } = getState().app;
//       const formData = new FormData();
//       formData.append('productId', data.id);
//       formData.append('file', data.file);
//       formData.append('live', LIVE + '');
//       let res = await fetch(API_URL + '/products/upload-3d-model', {
//         body: formData,
//         method: 'POST',
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });
//       if (res.status === 200) return await res.json();
//       return rejectWithValue({ error: true, errorCode: res.status });
//     } catch (e) {
//       return rejectWithValue({ error: true, errorCode: 900 });
//     }
//   },
// );
const productUploadExtractions_3d = createAsyncThunk(
  'UPLOAD_EXTRACTION_3D',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      const formData = new FormData();
      // formData.append('productId', data.id);
      formData.append('file', data.file);
      // formData.append('live', LIVE + '');
      let res = await fetch(API_URL + '/products/' + data.id + '/models/' + data.extensionType, {
        body: formData,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) return await res.json();
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const productDeleteExtractions_3d = createAsyncThunk(
  'DELETE_EXTRACTION_3D',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let res = await fetch(API_URL + '/products/' + data.id + '/models/' + data.extensionType + '/' + data.modelId, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) return await res.json();
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const setExtractionActive = createAsyncThunk(
  'EXTRACTION_ACTIVE',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let endPoint = '/extractions/set-extraction-inactive';
      if (!data.isActive) {
        endPoint = '/extractions/set-extraction-active';
      }
      let res = await fetch(API_URL + endPoint, {
        method: 'POST',
        body: JSON.stringify({ live: LIVE, filename: data.filename }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) return data;
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);
const productDeleteExtraction = createAsyncThunk(
  'REMOVING_EXTRACTION',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let res = await fetch(API_URL + '/extractions/delete-extraction', {
        method: 'POST',
        body: JSON.stringify({ live: LIVE, filename: data.filename }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) return data;
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);
const productDeleteModel3D = createAsyncThunk(
  'REMOVING_MODEL3D',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let res = await fetch(API_URL + '/products/delete-3d-model', {
        body: JSON.stringify({ live: true, modelId: data.modelId }),
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 200) return data;
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const addProductVariation = createAsyncThunk(
  'ADDING_Variation',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      const { file, productId, variantId } = data;

      let fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      fd.append('order', variantId);
      fd.append('description', file.size.toString());

      let res = await fetch(API_URL + `/products/${productId}/variants`, {
        method: 'POST',
        body: fd,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        return await res.json();
      }
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const productOrderVariations = createAsyncThunk(
  'ORDER_VARIATIONS',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { variantsSorted, variants, productId } = data;
      const { token } = getState().app;
      let res = await fetch(
        API_URL + `/products/${productId}/variants/order/sort`,
        {
          method: 'PUT',
          body: JSON.stringify({ variants }),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status === 200) {
        let response = await res.json();
        return { productId, variantsSorted, variants: response.variants };
      } else {
        return rejectWithValue(res.status);
      }
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const orderProducts = createAsyncThunk(
  'ORDER_PRODUCTS',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { productsSorted, products } = data;
      const { token } = getState().app;
      const { item } = getState().merchant;
      const merchantId = item.id;
      let res = await fetch(API_URL + `/merchants/products/order/sort`, {
        method: 'PUT',
        body: JSON.stringify({ products, merchantId }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 200) {
        return productsSorted;
      } else {
        return rejectWithValue(res.status);
      }
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const productDeleteVariation = createAsyncThunk(
  'REMOVING_VARIATION',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      const { productId, variantId } = data;
      let res = await fetch(
        API_URL + `/products/${productId}/variants/${variantId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status === 200) return data;
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);
const productUpdateExtraction = createAsyncThunk(
  'UPDATE_EXTRACTION',
  async (data, { getState, rejectWithValue }) => {
    try {
      const { token } = getState().app;
      let res = await fetch(API_URL + '/products/delete-3d-model', {
        body: JSON.stringify({ live: true, modelId: data.modelId }),
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 200) return data;
      return rejectWithValue({ error: true, errorCode: res.status });
    } catch (e) {
      return rejectWithValue({ error: true, errorCode: 900 });
    }
  },
);

const productSlice = createSlice({
  name: 'PRODUCTS',
  initialState: initialProductsState,
  reducers: {
    receivedProduct(state, action) {
      state.items.unshift(action.payload);
    },
    selectProduct(state, action) {
      state.selectedProduct = action.payload;
    },
    selectExtraction(state, action) {
      state.selectedExtraction = action.payload;
    },
    changePage(state, action) {
      state.page = 1;
      state.items = [];
    },
    resetStateProducts(state) {
      state.isFetchingProducts = true;
      state.isFetchingProductsPagin = true;
      state.errorPagin = false;
      state.paginator = false;
      state.isIndexed = false;
      state.isCreating = false;
      state.isRemoving = false;
      state.isActivating = false;
      state.isUpdating = false;
      state.items = [];
      state.itemsUpdated = 0;
      state.merchantId = undefined;
      state.errorProducts = null;
      state.errorCreatingProduct = null;
      state.selectedProduct = null;
      state.selectedExtraction = null;
      state.isUpdatingExtraction = false;
      state.errorUpdatingExtraction = false;
      state.isDeletingVariation = false;
      state.errorDeletingVariation = false;
      state.isCreatingVariation = false;
      state.errorCreatingVariation = false;
      state.isOrderingVariation = false;
      state.errorOrderingVariation = false;
      state.page = 1;
      state.showLoadMore = false;
      state.isOrderingProducts = false;
    }
  },
  extraReducers: {
    [requestProductsPagin.pending]: (state) => {
      state.isFetchingProductsPagin = true;
    },
    [requestProductsPagin.fulfilled]: (state, action) => {
      state.isFetchingProductsPagin = false;
      state.errorPagin = false;
      state.paginator = action.payload;
    },
    [requestProductsPagin.rejected]: (state, action) => {
      state.errorPagin = action.payload;
    },
    [requestProducts.pending]: (state) => {
      state.isFetchingProducts = true;
    },
    [requestProducts.fulfilled]: (state, action) => {
      state.isFetchingProducts = false;
      state.errorProducts = false;
      state.items.push(...action.payload);
      state.showLoadMore = action.payload.length === PRODUCTS_PER_PAGE;
      state.page = state.page + 1;
      state.itemsUpdated = new Date().getTime();
    },
    [requestProducts.rejected]: (state, action) => {
      state.errorProducts = action.payload;
    },
    [addProduct.pending]: (state) => {
      state.isCreating = true;
    },
    [addProduct.fulfilled]: (state) => {
      state.isCreating = false;
      state.errorCreatingProduct = false;
    },
    [addProduct.rejected]: (state, action) => {
      state.isCreating = false;
      state.errorCreatingProduct = action.payload;
    },
    [requestProduct.pending]: (state) => {
      state.isFetchingProducts = true;
    },
    [requestProduct.fulfilled]: (state, action) => {
      state.isFetchingProducts = false;
      state.errorProducts = false;
      state.selectedProduct = action.payload;
    },
    [requestProduct.rejected]: (state, action) => {
      state.isFetchingProducts = false;
      state.errorProducts = action.payload;
    },
    [editProduct.pending]: (state) => {
      state.isUpdating = true;
    },
    [editProduct.fulfilled]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = false;
      let p = action.payload;
      state.selectedProduct = p;
      state.items = state.items.map((product) =>
        product.id === p.id ? p : product,
      );
    },
    [editProduct.rejected]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = action.payload;
    },
    [editProductImage.pending]: (state) => {
      state.isUpdating = true;
      state.errorProducts = false;
    },
    [editProductImage.fulfilled]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = false;
      state.selectedProduct = action.payload;
    },
    [editProductImage.rejected]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = action.payload;
    },
    [productUploadExtractions.pending]: (state) => {
      state.isUpdating = true;
      state.errorProducts = false;
    },
    [productUploadExtractions.fulfilled]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = false;
      let result = action.payload;
      state.selectedProduct = result[0];
      state.items = state.items.map((p) =>
        p.id === result[0].id ? result[0] : p,
      );
    },
    [productUploadExtractions.rejected]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = action.payload;
    },
    // PRODUCT UPLOAD 3D MODEL 
    [productUploadExtractions_3d.pending]: (state) => {
      state.isUpdating = true;
    },
    [productUploadExtractions_3d.fulfilled]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = false;
      state.selectedProduct = action.payload;
    },
    [productUploadExtractions_3d.rejected]: (state, action) => {
      state.isUpdating = false;
      state.errorProducts = action.payload;
    },
    // PRODUCT DELETE 3D MODEL 
    [productDeleteExtractions_3d.pending]: (state) => {
      state.isRemoving = true;
      state.errorProducts = false;
    },
    [productDeleteExtractions_3d.fulfilled]: (state, action) => {
      state.isRemoving = false;
      state.errorProducts = false;
      state.selectedProduct = action.payload;
    },
    [productDeleteExtractions_3d.rejected]: (state, action) => {
      state.isRemoving = false;
      state.errorProducts = action.payload;
    },
    //
    [setExtractionActive.pending]: (state) => {
      state.isActivating = true;
      state.errorProducts = false;
    },
    [setExtractionActive.fulfilled]: (state, action) => {
      state.isActivating = false;
      state.errorProducts = false;
      let res = action.payload;
      const newStateActive = !res.isActive;
      state.selectedProduct.extractions = state.selectedProduct.extractions.map(
        (ext) => {
          if (ext.filename === res.filename) {
            return { ...ext, active: newStateActive };
          } else if (newStateActive === true) {
            return { ...ext, active: false };
          }
          return ext;
        },
      );
    },
    [setExtractionActive.rejected]: (state, action) => {
      state.isActivating = false;
      state.errorProducts = action.payload;
    },
    [productDeleteExtraction.pending]: (state) => {
      state.isRemoving = true;
      state.errorProducts = false;
    },
    [productDeleteExtraction.fulfilled]: (state, action) => {
      state.isRemoving = false;
      state.errorProducts = false;
      let res = action.payload;
      const ex =
        state.selectedProduct.extractions.filter(
          (ext) => ext.filename !== res.filename,
        );
      state.selectedProduct.extractions = ex;
      state.items = state.items.map(
        (p) => {
          if (p.id !== state.selectedProduct.id)
            return p;
          return state.selectedProduct
        }
      );
    },
    [productDeleteExtraction.rejected]: (state, action) => {
      state.isRemoving = false;
      state.errorProducts = action.payload;
    },
    [productDeleteModel3D.pending]: (state) => {
      state.isRemoving = true;
      state.errorProducts = false;
    },
    [productDeleteModel3D.fulfilled]: (state, action) => {
      state.isRemoving = false;
      state.errorProducts = false;
      let res = action.payload;

      state.selectedProduct.models = state.selectedProduct.models.filter(
        (model) => model.id !== res.modelId,
      );
    },
    [productDeleteModel3D.rejected]: (state, action) => {
      state.isRemoving = false;
      state.errorProducts = action.payload;
    },
    [removeProductById.pending]: (state) => {
      state.isFetchingProducts = true;
      state.errorProducts = false;
    },
    [removeProductById.fulfilled]: (state, action) => {
      state.isFetchingProducts = false;
      state.errorProducts = false;
      state.items = state.items.filter(
        (product) => product.id !== action.payload.productId,
      );
      if (
        state.selectedProduct !== null &&
        state.selectedProduct !== undefined &&
        true// state.selectedProduct !== {}
      )
        state.selectedProduct =
          state.selectedProduct.id === action.payload.productId
            ? null
            : state.selectedProduct;
    },
    [removeProductById.rejected]: (state, action) => {
      state.isFetchingProducts = true;
      state.errorProducts = action.payload;
    },
    [updateProductExtraction.pending]: (state) => {
      state.isUpdatingExtraction = true;
      state.errorUpdatingExtraction = false;
    },
    [updateProductExtraction.fulfilled]: (state, action) => {
      state.isUpdatingExtraction = false;
      state.errorUpdatingExtraction = false;
      state.selectedProduct = action.payload;

      state.items = state.items.map((product) => {
        if (product.id === action.payload.productId) return action.payload;
        return product;
      });
    },
    [updateProductExtraction.rejected]: (state, action) => {
      state.isUpdatingExtraction = true;
      state.errorUpdatingExtraction = action.payload;
    },
    [updateProductVariantExtraction.pending]: (state) => {
      state.isUpdatingExtraction = true;
      state.errorUpdatingExtraction = false;
    },
    [updateProductVariantExtraction.fulfilled]: (state, action) => {
      state.isUpdatingExtraction = false;
      state.errorUpdatingExtraction = false;
      state.selectedProduct = action.payload;

      state.items = state.items.map((product) => {
        if (product.id === action.payload.productId) return action.payload;
        return product;
      });
    },
    [updateProductVariantExtraction.rejected]: (state, action) => {
      state.isUpdatingExtraction = true;
      state.errorUpdatingExtraction = action.payload;
    },
    [addProductVariation.pending]: (state) => {
      state.isCreatingVariation = true;
    },
    [addProductVariation.fulfilled]: (state) => {
      state.isCreatingVariation = false;
    },
    [addProductVariation.rejected]: (state) => {
      state.errorCreatingVariation = true;
      state.isCreatingVariation = false;
    },
    [productDeleteVariation.pending]: (state) => {
      state.isDeletingVariation = true;
    },
    [productDeleteVariation.fulfilled]: (state) => {
      state.isDeletingVariation = false;
    },
    [productDeleteVariation.rejected]: (state) => {
      state.errorDeletingVariation = true;
      state.isDeletingVariation = false;
    },
    [orderProducts.pending]: (state) => {
      state.isOrderingProducts = true;
    },
    [orderProducts.fulfilled]: (state, action) => {
      state.isOrderingProducts = false;
      state.items = action.payload;
    },
    [orderProducts.rejected]: (state) => {
      state.isOrderingProducts = false;
    },
    [productOrderVariations.pending]: (state) => {
      state.isOrderingVariation = true;
    },
    [productOrderVariations.fulfilled]: (state, action) => {
      state.isOrderingVariation = false;
      const { productId, variantsSorted } = action.payload;
      state.items = state.items.map((item) => {
        if (item.id === productId) return { ...item, variants: variantsSorted };
        return item;
      });
    },
    [productOrderVariations.rejected]: (state) => {
      state.errorOrderingVariation = true;
      state.isOrderingVariation = false;
    },
  },
});

export const { changePage, receivedProduct, selectProduct, selectExtraction, resetStateProducts } =
  productSlice.actions;
export {
  requestProductsPagin,
  requestProducts,
  addProduct,
  requestProduct,
  editProduct,
  editProductImage,
  productUploadExtractions,
  productUploadExtractions_3d,
  productDeleteExtractions_3d,
  setExtractionActive,
  productDeleteExtraction,
  productDeleteModel3D,
  removeProductById,
  productUpdateExtraction,
  updateProductExtraction,
  updateProductVariantExtraction,
  addProductVariation,
  productDeleteVariation,
  productOrderVariations,
  orderProducts,
};
export default productSlice.reducer;
